import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { AnimatePresence, motion } from 'framer-motion';
import DataEditor, { GridCellKind } from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { ChartBarIcon, ChevronDown, Download, FileSpreadsheet, File, BarChart3, Table2, PanelsTopLeft } from 'lucide-react';

const VIEW_MODES = [
  { value: 'both', label: 'Both', icon: PanelsTopLeft },
  { value: 'chart', label: 'Chart', icon: BarChart3 },
  { value: 'table', label: 'Table', icon: Table2 },
];

const RollingStraightedge = ({
  events = [],
  isReversed = false,
  formatStation,
  session
}) => {
  const [viewMode, setViewMode] = useState('both');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Analysis boundaries
  const analysisStart = session?.analysis_start_station ?? session?.start_station_ft ?? 0;
  const analysisEnd = session?.analysis_end_station ?? session?.end_station_ft ?? 0;

  // Process events data
  const processedEventsData = useMemo(() => {
    return events?.map(event => ({
      distance: parseFloat(event.from_station_ft) || 0,
      station: formatStation(parseFloat(event.from_station_ft) || 0),
      peakStation: parseFloat(event.peak_station_ft) || 0,
      peakStationLabel: formatStation(parseFloat(event.peak_station_ft) || 0),
      endStation: parseFloat(event.to_station_ft) || 0,
      endStationLabel: formatStation(parseFloat(event.to_station_ft) || 0),
      magnitude: parseFloat(event.magnitude_in) || 0,
      eventType: event.event_type,
      wheelpath: event.wheelpath,
      eventId: event.event_id,
      length: (parseFloat(event.to_station_ft) || 0) - (parseFloat(event.from_station_ft) || 0)
    })).sort((a, b) => a.distance - b.distance) || [];
  }, [events, formatStation]);

  // Filter for surface events (Bump and Dip only)
  const surfaceEvents = useMemo(() => 
    processedEventsData.filter(e => e.eventType === 'Bump' || e.eventType === 'Dip'),
    [processedEventsData]
  );

  const bumpEvents = useMemo(() => 
    surfaceEvents.filter(e => e.eventType === 'Bump'),
    [surfaceEvents]
  );

  const dipEvents = useMemo(() => 
    surfaceEvents.filter(e => e.eventType === 'Dip'),
    [surfaceEvents]
  );

  // Table data sorted by station
  const tableData = useMemo(() => {
    return [...surfaceEvents].sort((a, b) => a.distance - b.distance);
  }, [surfaceEvents]);

  // Export functions
  const exportToCSV = useCallback(() => {
    const headers = ['Event ID', 'Type', 'Wheelpath', 'Start Station', 'Peak Station', 'End Station', 'Length (ft)', 'Magnitude (in)'];
    const rows = tableData.map(row => [
      row.eventId,
      row.eventType,
      row.wheelpath || '',
      row.station,
      row.peakStationLabel,
      row.endStationLabel,
      row.length.toFixed(1),
      row.magnitude.toFixed(4)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'rolling_straightedge_events.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }, [tableData]);

  const exportToTXT = useCallback(() => {
    const headers = ['Event ID', 'Type', 'Wheelpath', 'Start Station', 'Peak Station', 'End Station', 'Length (ft)', 'Magnitude (in)'];
    const colWidths = [12, 8, 12, 14, 14, 14, 12, 14];

    const padCell = (str, width) => str.toString().padEnd(width);
    const headerLine = headers.map((h, i) => padCell(h, colWidths[i])).join('');
    const separatorLine = colWidths.map(w => '-'.repeat(w)).join('');

    const rows = tableData.map(row => [
      padCell(row.eventId || '-', colWidths[0]),
      padCell(row.eventType, colWidths[1]),
      padCell(row.wheelpath || '-', colWidths[2]),
      padCell(row.station, colWidths[3]),
      padCell(row.peakStationLabel, colWidths[4]),
      padCell(row.endStationLabel, colWidths[5]),
      padCell(row.length.toFixed(1), colWidths[6]),
      padCell(row.magnitude.toFixed(4), colWidths[7])
    ].join(''));

    const txtContent = [
      'ROLLING STRAIGHTEDGE EVENTS REPORT',
      '='.repeat(100),
      '',
      `Generated: ${new Date().toLocaleString()}`,
      `Total Events: ${tableData.length}`,
      `Bumps: ${bumpEvents.length}`,
      `Dips: ${dipEvents.length}`,
      '',
      headerLine,
      separatorLine,
      ...rows
    ].join('\n');

    const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'rolling_straightedge_events.txt';
    link.click();
    URL.revokeObjectURL(link.href);
  }, [tableData, bumpEvents.length, dipEvents.length]);

  // Build Plotly data for diverging stem chart
  const { plotData, layout } = useMemo(() => {
    const sortedEvents = isReversed 
      ? [...surfaceEvents].sort((a, b) => b.peakStation - a.peakStation)
      : [...surfaceEvents].sort((a, b) => a.peakStation - b.peakStation);

    // Calculate Y axis range (symmetric around zero)
    const maxMagnitude = sortedEvents.length > 0 
      ? Math.max(...sortedEvents.map(e => e.magnitude)) 
      : 0.5;
    const yPadding = maxMagnitude * 0.15;
    const yMax = maxMagnitude + yPadding;

    // X-axis padding
    const span = analysisEnd - analysisStart;
    const padding = span * 0.02;

    // Create stem traces
    const traces = [];

    // Bump stems (positive)
    const bumps = sortedEvents.filter(e => e.eventType === 'Bump');
    if (bumps.length > 0) {
      // Stem lines for bumps
      bumps.forEach(event => {
        traces.push({
          type: 'scatter',
          mode: 'lines',
          x: [event.peakStation, event.peakStation],
          y: [0, event.magnitude],
          line: { color: '#dc2626', width: 2 },
          hoverinfo: 'skip',
          showlegend: false
        });
      });

      // Markers for bumps
      traces.push({
        type: 'scatter',
        mode: 'markers',
        name: `Bumps (${bumps.length})`,
        x: bumps.map(e => e.peakStation),
        y: bumps.map(e => e.magnitude),
        marker: {
          color: '#ef4444',
          size: 10,
          symbol: 'circle',
          line: { color: '#dc2626', width: 1.5 }
        },
        customdata: bumps.map(e => [
          e.station,
          e.peakStationLabel,
          e.endStationLabel,
          e.magnitude,
          e.wheelpath,
          e.eventId,
          e.length
        ]),
        hovertemplate:
          '<b>Bump</b><br>' +
          'Peak: %{customdata[1]}<br>' +
          'Magnitude: %{customdata[3]:.4f} in<br>' +
          'Wheelpath: %{customdata[4]}<br>' +
          'Span: %{customdata[0]} → %{customdata[2]}<br>' +
          'Length: %{customdata[6]:.1f} ft<br>' +
          '<extra></extra>'
      });
    }

    // Dip stems (negative)
    const dips = sortedEvents.filter(e => e.eventType === 'Dip');
    if (dips.length > 0) {
      // Stem lines for dips
      dips.forEach(event => {
        traces.push({
          type: 'scatter',
          mode: 'lines',
          x: [event.peakStation, event.peakStation],
          y: [0, -event.magnitude],
          line: { color: '#1e40af', width: 2 },
          hoverinfo: 'skip',
          showlegend: false
        });
      });

      // Markers for dips
      traces.push({
        type: 'scatter',
        mode: 'markers',
        name: `Dips (${dips.length})`,
        x: dips.map(e => e.peakStation),
        y: dips.map(e => -e.magnitude),
        marker: {
          color: '#3b82f6',
          size: 10,
          symbol: 'circle',
          line: { color: '#1e40af', width: 1.5 }
        },
        customdata: dips.map(e => [
          e.station,
          e.peakStationLabel,
          e.endStationLabel,
          e.magnitude,
          e.wheelpath,
          e.eventId,
          e.length
        ]),
        hovertemplate:
          '<b>Dip</b><br>' +
          'Peak: %{customdata[1]}<br>' +
          'Magnitude: %{customdata[3]:.4f} in<br>' +
          'Wheelpath: %{customdata[4]}<br>' +
          'Span: %{customdata[0]} → %{customdata[2]}<br>' +
          'Length: %{customdata[6]:.1f} ft<br>' +
          '<extra></extra>'
      });
    }

    // Generate tick values
    const tickCount = 12;
    const tickStep = (analysisEnd - analysisStart) / tickCount;
    const tickVals = [];
    const tickText = [];
    for (let i = 0; i <= tickCount; i++) {
      const val = analysisStart + (i * tickStep);
      tickVals.push(val);
      tickText.push(formatStation(val));
    }

    const _layout = {
      xaxis: {
        title: { text: isReversed ? 'Station (Work Direction →)' : 'Station', font: { size: 12 } },
        tickmode: 'array',
        tickvals: tickVals,
        ticktext: tickText,
        tickangle: -45,
        tickfont: { size: 10 },
        range: isReversed 
          ? [analysisEnd + padding, analysisStart - padding]
          : [analysisStart - padding, analysisEnd + padding],
        zeroline: false,
        gridcolor: '#e5e7eb'
      },
      yaxis: {
        title: { text: 'Magnitude (in)', font: { size: 12 } },
        tickfont: { size: 10 },
        range: [-yMax, yMax],
        zeroline: true,
        zerolinecolor: '#374151',
        zerolinewidth: 2,
        gridcolor: '#e5e7eb'
      },
      shapes: [
        // Shaded region for bumps (above zero)
        {
          type: 'rect',
          xref: 'paper',
          yref: 'y',
          x0: 0,
          x1: 1,
          y0: 0,
          y1: yMax,
          fillcolor: 'rgba(239, 68, 68, 0.03)',
          line: { width: 0 }
        },
        // Shaded region for dips (below zero)
        {
          type: 'rect',
          xref: 'paper',
          yref: 'y',
          x0: 0,
          x1: 1,
          y0: -yMax,
          y1: 0,
          fillcolor: 'rgba(59, 130, 246, 0.03)',
          line: { width: 0 }
        }
      ],
      annotations: [
        {
          xref: 'paper',
          yref: 'y',
          x: 0.01,
          y: yMax * 0.9,
          text: 'BUMPS ↑',
          showarrow: false,
          font: { size: 11, color: '#dc2626' }
        },
        {
          xref: 'paper',
          yref: 'y',
          x: 0.01,
          y: -yMax * 0.9,
          text: 'DIPS ↓',
          showarrow: false,
          font: { size: 11, color: '#1e40af' }
        }
      ],
      margin: { t: 40, r: 40, b: 80, l: 60 },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      hovermode: 'closest',
      showlegend: true,
      legend: {
        orientation: 'h',
        yanchor: 'bottom',
        y: 1.02,
        xanchor: 'right',
        x: 1
      }
    };

    return { plotData: traces, layout: _layout };
  }, [surfaceEvents, isReversed, analysisStart, analysisEnd, formatStation]);

  const config = {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['lasso2d', 'select2d']
  };

  const formatMagnitude = (value) => {
    if (value === null || value === undefined) return '—';
    return value.toFixed(4);
  };

  const formatLength = (value) => {
    if (value === null || value === undefined) return '—';
    return value.toFixed(1);
  };

  const showChart = viewMode === 'both' || viewMode === 'chart';
  const showTable = viewMode === 'both' || viewMode === 'table';

  const eventGridColumns = useMemo(() => {
    const dataColumns = [
      { id: 'eventId', title: 'Event ID', width: 130 },
      { id: 'eventType', title: 'Type', width: 100 },
      { id: 'wheelpath', title: 'Wheelpath', width: 110 },
      { id: 'startStation', title: 'Start Station', width: 135 },
      { id: 'peakStation', title: 'Peak Station', width: 135 },
      { id: 'endStation', title: 'End Station', width: 135 },
      { id: 'length', title: 'Length (ft)', width: 110 },
      { id: 'magnitude', title: 'Magnitude (in)', width: 130 },
    ];
    const fillerColumns = Array.from({ length: 8 }, (_, index) => ({
      id: `blank-${index + 1}`,
      title: '',
      width: 110,
      grow: 1,
    }));

    return [...dataColumns, ...fillerColumns];
  }, []);

  const eventGridRows = useMemo(() => tableData.map(row => ({
    eventId: row.eventId != null ? String(row.eventId) : '',
    eventType: row.eventType != null ? String(row.eventType) : '',
    wheelpath: row.wheelpath != null ? String(row.wheelpath) : '',
    startStation: row.station != null ? String(row.station) : '',
    peakStation: row.peakStationLabel != null ? String(row.peakStationLabel) : '',
    endStation: row.endStationLabel != null ? String(row.endStationLabel) : '',
    length: formatLength(row.length),
    magnitude: formatMagnitude(row.magnitude),
    isBump: row.eventType === 'Bump',
    isDip: row.eventType === 'Dip',
  })), [tableData]);

  const getEventCellContent = useCallback(([col, row]) => {
    const gridRow = eventGridRows[row];
    const column = eventGridColumns[col];
    const value = gridRow?.[column?.id] ?? '';
    const themeOverride = gridRow?.isBump
      ? { bgCell: '#fef2f2', bgCellMedium: '#fee2e2' }
      : gridRow?.isDip
        ? { bgCell: '#eff6ff', bgCellMedium: '#dbeafe' }
        : undefined;

    return {
      kind: GridCellKind.Text,
      data: value,
      displayData: value,
      allowOverlay: false,
      readonly: true,
      themeOverride,
    };
  }, [eventGridColumns, eventGridRows]);

  return (
    <div className="bg-white ">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 h-20">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Rolling Straightedge Events
              {isReversed && (
                <span className="text-sm font-normal text-blue-600 ml-2">(Reverse Direction)</span>
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {bumpEvents.length} bumps, {dipEvents.length} dips detected
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="inline-flex h-9 items-center rounded border border-gray-300 bg-white p-0.5">
              {VIEW_MODES.map(option => {
                const Icon = option.icon;
                const isActive = viewMode === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setViewMode(option.value)}
                    className={`h-7 px-2.5 text-xs font-medium rounded flex items-center gap-1.5 transition-colors ${
                      isActive
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* Export Dropdown */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                type="button"
                onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                className="relative w-32 bg-white border border-gray-300 rounded px-3 py-1.5 text-left text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>

              {exportDropdownOpen && (
                <div className="absolute z-10 mt-1 w-48 bg-white border border-gray-200 rounded shadow-lg right-0">
                  <ul className="py-1">
                    <li>
                      <button
                        type="button"
                        onClick={() => {
                          exportToCSV();
                          setExportDropdownOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <FileSpreadsheet className="w-4 h-4 text-green-600" />
                        <div>
                          <div className="font-medium">CSV</div>
                          <div className="text-xs text-gray-500">Spreadsheet format</div>
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={() => {
                          exportToTXT();
                          setExportDropdownOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <File className="w-4 h-4 text-gray-600" />
                        <div>
                          <div className="font-medium">Text File</div>
                          <div className="text-xs text-gray-500">Plain text format</div>
                        </div>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden">
        {surfaceEvents.length > 0 ? (
          <AnimatePresence initial={false}>
            {showChart && (
              <motion.section
                key="chart"
                layout
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="px-6 pt-6 pb-6"
              >
                <div style={{ height: '450px', width: '100%' }}>
                  <Plot
                    data={plotData}
                    layout={layout}
                    config={config}
                    style={{ width: '100%', height: '100%' }}
                    useResizeHandler={true}
                  />
                </div>
              </motion.section>
            )}

            {showTable && (
              <motion.section
                key="table"
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className={`${showChart ? 'border-t border-gray-200' : ''}`}
              >
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-700">Event Data</h4>
                  <p className="text-xs text-gray-500">{tableData.length} events total</p>
                </div>
                <div className={viewMode === 'table' ? 'h-[calc(100vh-220px)] min-h-[520px]' : 'h-80'}>
                  <DataEditor
                    columns={eventGridColumns}
                    rows={eventGridRows.length}
                    getCellContent={getEventCellContent}
                    getCellsForSelection={true}
                    rowMarkers="number"
                    rowHeight={34}
                    headerHeight={38}
                    smoothScrollX={true}
                    smoothScrollY={true}
                    rangeSelect="multi-rect"
                    freezeColumns={1}
                    theme={{
                      accentColor: '#2563eb',
                      accentLight: 'rgba(37, 99, 235, 0.12)',
                      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                      cellHorizontalPadding: 12,
                      headerFontStyle: '600 12px',
                      baseFontStyle: '13px',
                    }}
                  />
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        ) : (
          <div className="text-center py-12 px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <ChartBarIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Events Detected</h4>
            <p className="text-sm text-gray-500">No bump or dip events were found in this session.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RollingStraightedge;
